/**
 * Cognito Hosted UI auth (OAuth2 Authorization Code + PKCE).
 *
 * Framework-agnostic on purpose: both AuthContext (React state) and
 * services/api.js (401 retry / proactive token refresh) call into this
 * module directly, so there is exactly one place that knows how to talk to
 * Cognito and exactly one in-flight refresh at a time.
 */

const ID_TOKEN_KEY = 'collections_id_token';
const ACCESS_TOKEN_KEY = 'collections_access_token';
const REFRESH_TOKEN_KEY = 'collections_refresh_token';
const CODE_VERIFIER_KEY = 'collections_code_verifier';

export const AUTH_CONFIG = {
  region: import.meta.env.VITE_COGNITO_REGION || 'us-east-1',
  clientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '',
  cognitoDomain: import.meta.env.VITE_COGNITO_DOMAIN || 'collections-dev-443370675683',
};

function tokenEndpoint() {
  const { cognitoDomain, region } = AUTH_CONFIG;
  return `https://${cognitoDomain}.auth.${region}.amazoncognito.com/oauth2/token`;
}

function base64UrlEncode(buffer) {
  let str = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    str += String.fromCharCode(bytes[i]);
  }
  return btoa(str)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function generateCodeVerifier() {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

async function generateCodeChallenge(verifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(hash));
}

export function getIdToken() {
  return sessionStorage.getItem(ID_TOKEN_KEY);
}

export function getRefreshToken() {
  return sessionStorage.getItem(REFRESH_TOKEN_KEY);
}

export function isTokenValid(token) {
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return Date.now() < payload.exp * 1000;
  } catch {
    return false;
  }
}

function storeTokens(tokens, fallbackRefreshToken) {
  sessionStorage.setItem(ID_TOKEN_KEY, tokens.id_token);
  sessionStorage.setItem(ACCESS_TOKEN_KEY, tokens.access_token);
  // Cognito only issues a new refresh_token when refresh-token rotation is
  // enabled on the app client; otherwise keep the one we already have.
  const refreshToken = tokens.refresh_token || fallbackRefreshToken;
  if (refreshToken) {
    sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
}

export function clearTokens() {
  sessionStorage.removeItem(ID_TOKEN_KEY);
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(CODE_VERIFIER_KEY);
}

/**
 * Redirect to the Cognito hosted UI to sign in. `returnUrl` travels through
 * as the OAuth `state` param so Callback can send the user back to where
 * they were instead of always landing on `/`.
 *
 * `state` is `encodeURIComponent`-ed before it's set. Cognito's hosted UI
 * does not safely round-trip a `state` value that itself contains `&`/`=`/
 * `?` — COL-87's URL state (`?category=&q=&item=`) is exactly that shape,
 * and verified live: everything past the first embedded `&` got silently
 * dropped on the way back from a real login, leaving `?category=Travel`
 * but losing `q=` and `item=`. Encoding first collapses the value to a
 * string of only unreserved characters (plus `%`) with no delimiter
 * Cognito's redirect construction can misparse; `handleCallback` decodes it
 * back (COL-154).
 */
export async function redirectToLogin(returnUrl = '/') {
  const { cognitoDomain, region, clientId } = AUTH_CONFIG;
  const redirectUri = `${window.location.origin}/auth/callback`;

  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  sessionStorage.setItem(CODE_VERIFIER_KEY, codeVerifier);

  const authUrl = new URL(`https://${cognitoDomain}.auth.${region}.amazoncognito.com/oauth2/authorize`);
  authUrl.searchParams.set('response_type', 'code');
  authUrl.searchParams.set('client_id', clientId);
  authUrl.searchParams.set('redirect_uri', redirectUri);
  authUrl.searchParams.set('scope', 'email openid profile');
  authUrl.searchParams.set('code_challenge', codeChallenge);
  authUrl.searchParams.set('code_challenge_method', 'S256');
  authUrl.searchParams.set('state', encodeURIComponent(returnUrl));

  window.location.href = authUrl.toString();
}

/**
 * Exchange an OAuth authorization code for tokens and persist them.
 */
export async function exchangeCodeForTokens(code, codeVerifier) {
  const { clientId } = AUTH_CONFIG;
  const redirectUri = `${window.location.origin}/auth/callback`;

  const response = await fetch(tokenEndpoint(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: clientId,
      code,
      redirect_uri: redirectUri,
      code_verifier: codeVerifier,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Token exchange failed: ${errorText}`);
  }

  const tokens = await response.json();
  storeTokens(tokens);
  return tokens;
}

/**
 * Validate a post-login return destination before it's ever handed to
 * `navigate()`. Two of the three places this value comes from are
 * attacker-influencable: the OAuth `state` param round-trips through
 * Cognito's hosted UI (an attacker can link a victim straight to the
 * `/oauth2/authorize` endpoint with a crafted `state`), and a `redirect`
 * query param on `/login` is copy-pasteable into a shared link. Only ever
 * accept a same-origin relative path — never an absolute URL, a
 * protocol-relative `//host` (or `/\host`, which some browsers resolve
 * identically to `//host`), or a `javascript:`/other-scheme value (COL-154).
 */
export function sanitizeReturnPath(value, fallback = '/') {
  if (typeof value !== 'string' || value.length === 0) {
    return fallback;
  }

  let url;
  try {
    url = new URL(value, window.location.origin);
  } catch {
    return fallback;
  }

  if (url.origin !== window.location.origin) {
    return fallback;
  }

  // Recompose from the parsed parts rather than trusting the raw string.
  return `${url.pathname}${url.search}${url.hash}`;
}

export function buildLogoutUrl(returnUrl = '/') {
  const { cognitoDomain, region, clientId } = AUTH_CONFIG;
  const logoutUrl = new URL(`https://${cognitoDomain}.auth.${region}.amazoncognito.com/logout`);
  logoutUrl.searchParams.set('client_id', clientId);
  logoutUrl.searchParams.set('logout_uri', `${window.location.origin}${returnUrl}`);
  return logoutUrl.toString();
}

/**
 * Revoke a refresh token at Cognito's public, unauthenticated /oauth2/revoke
 * endpoint (COL-192) -- kills the *this-browser* session server-side, so a
 * copy of the token pulled from storage/logs after logout can't be replayed
 * for a new access/id token. Deliberately NOT a Lambda endpoint: a route on
 * our API would just proxy this same public call while also requiring the
 * caller to hold the token it's killing.
 *
 * Fire-and-forget by design -- logout must complete even if Cognito is
 * unreachable, so every failure (network error, non-2xx, timeout) is
 * swallowed rather than thrown. Bounded by a 2s AbortController timeout so a
 * hung request can never delay the redirect to Hosted UI /logout.
 */
export async function revokeRefreshToken(refreshToken) {
  if (!refreshToken) return;

  const { cognitoDomain, region, clientId } = AUTH_CONFIG;
  const revokeUrl = `https://${cognitoDomain}.auth.${region}.amazoncognito.com/oauth2/revoke`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 2000);

  try {
    await fetch(revokeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ token: refreshToken, client_id: clientId }),
      signal: controller.signal,
      keepalive: true,
    });
  } catch (e) {
    console.error('Refresh token revocation failed (non-fatal):', e);
  } finally {
    clearTimeout(timeoutId);
  }
}

// Single in-flight refresh shared by every caller (AuthContext, ApiService).
// Prevents a stampede of parallel refresh requests when several API calls
// hit a 401 at the same time.
let refreshPromise = null;

/**
 * Refresh the id/access tokens using the stored refresh token.
 * Resolves with the new id_token. Rejects if there is no refresh token or
 * the refresh request itself fails — callers must treat rejection as a
 * terminal auth failure (redirect to login), never retry it themselves.
 */
export function refreshTokens() {
  if (refreshPromise) return refreshPromise;

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return Promise.reject(new Error('No refresh token available'));
  }

  const attempt = (async () => {
    const response = await fetch(tokenEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: AUTH_CONFIG.clientId,
        refresh_token: refreshToken,
      }),
    });

    if (!response.ok) {
      throw new Error(`Token refresh failed: HTTP ${response.status}`);
    }

    const tokens = await response.json();
    storeTokens(tokens, refreshToken);
    return tokens.id_token;
  })();

  // The cleanup lives on the same promise we hand back, so whoever awaits
  // refreshTokens() is also the one attaching the rejection handler — no
  // separate, uncaught `.finally()` chain left dangling on failure.
  refreshPromise = attempt.finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}
